import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const EMOJI_GROUPS = [
  {
    label: 'صورت',
    items: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😍', '🥰', '😘', '😋', '😜', '🤪', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '😢', '😭', '😤', '😠', '😡', '🤯', '😳', '😱', '🤗', '🤔', '🤭', '😴', '😷', '🤒'],
  },
  {
    label: 'واکنش',
    items: ['👍', '👎', '👏', '🙌', '🤝', '🙏', '✌️', '🤞', '👌', '👊', '👋', '💪', '🫶', '❤️', '🧡', '💛', '💚', '💙', '💜', '💔', '💯', '🔥', '✨', '⭐', '🎉', '🎊', '✅', '❌', '⚠️', '👀'],
  },
  {
    label: 'کار',
    items: ['💬', '📝', '📦', '🚀', '💡', '🎯', '⏰', '📞', '📧', '📎', '📁', '📅', '🔧', '🛠️', '💻', '📱', '📌', '🗂️', '🧾', '✅'],
  },
];

const PANEL_W = 280;
const PANEL_H = 260;

export function insertEmojiAtCursor(el, value, emoji) {
  const text = value ?? '';
  if (!el || typeof el.selectionStart !== 'number') {
    const next = text + emoji;
    return { next, pos: next.length };
  }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const next = text.slice(0, start) + emoji + text.slice(end);
  return { next, pos: start + emoji.length };
}

export function applyEmojiInsert(el, value, emoji, onChange) {
  const { next, pos } = insertEmojiAtCursor(el, value, emoji);
  onChange?.(next);
  requestAnimationFrame(() => {
    if (!el) return;
    el.focus();
    try { el.setSelectionRange(pos, pos); } catch { /* input type may not support range */ }
  });
  return next;
}

function panelStyleFor(btnRect) {
  const gap = 6;
  const left = Math.min(
    Math.max(8, btnRect.right - PANEL_W),
    window.innerWidth - PANEL_W - 8,
  );
  const spaceAbove = btnRect.top;
  const openUp = spaceAbove > PANEL_H + 16;
  const top = openUp
    ? btnRect.top - PANEL_H - gap
    : btnRect.bottom + gap;
  return {
    position: 'fixed',
    top: Math.max(8, top),
    left,
    width: PANEL_W,
    maxHeight: PANEL_H,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(15,23,42,0.16)',
    padding: 10,
    zIndex: 2000,
    direction: 'rtl',
  };
}

export function EmojiPickerButton({ onPick }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const updatePos = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setPos(panelStyleFor(rect));
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={btnRef}
        type="button"
        title="ایموجی"
        aria-label="درج ایموجی"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 34, height: 34, borderRadius: 8,
          border: `1.5px solid ${open ? '#93c5fd' : '#e2e8f0'}`,
          background: open ? '#EDF3FC' : '#fff',
          cursor: 'pointer', fontSize: 18, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
        }}
      >
        😊
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} style={pos}>
          {EMOJI_GROUPS.map((g) => (
            <div key={g.label} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>{g.label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 2 }}>
                {g.items.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => { onPick(emoji); setOpen(false); }}
                    style={{
                      width: 30, height: 30, border: 'none', background: 'transparent',
                      cursor: 'pointer', fontSize: 18, borderRadius: 6, padding: 0, lineHeight: 1,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export function EmojiBar({ inputRef, value, onInsert, extra, style: extraStyle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...extraStyle }}>
      <EmojiPickerButton onPick={(emoji) => {
        applyEmojiInsert(inputRef?.current, value, emoji, onInsert);
      }} />
      {extra}
    </div>
  );
}

/** Emoji button sitting on the corner of a textarea/input so it is clearly next to the box. */
export function EmojiFieldWrap({ inputRef, value, onInsert, children, padForButton = true }) {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <div style={{ position: 'absolute', bottom: padForButton ? 8 : 6, left: 8, zIndex: 2 }}>
        <EmojiPickerButton onPick={(emoji) => {
          applyEmojiInsert(inputRef?.current, value, emoji, onInsert);
        }} />
      </div>
    </div>
  );
}
